/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowLeft, ArrowRight, User, Star, MapPin, BadgeCheck, ShieldCheck,
    TrendingUp, MessageSquare, Briefcase, Sparkles, Building2, Check,
    Compass, ClipboardList,
} from 'lucide-react';
import { ConvNav } from './landing/ConvNav';
import { ConvFooter } from './landing/ConvFooter';
import { DemoRequestModal } from './landing/DemoRequestModal';
import { InviteAgencyModal } from './landing/InviteAgencyModal';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const BENEFITS = [
    {
        icon: ClipboardList,
        title: 'Valorisez le travail réalisé pendant tout l\'accompagnement',
        desc: "Chaque visite, échange et étape menée pour le vendeur devient une preuve de votre engagement professionnel.",
    },
    {
        icon: Star,
        title: 'Recevez des évaluations liées à des dossiers réels',
        desc: "Vos avis sont attachés à des accompagnements vendeurs vérifiés dans Homly — pas à des commentaires anonymes.",
    },
    {
        icon: Compass,
        title: 'Montrez vos spécialités',
        desc: "Types de biens, quartiers, profils vendeurs : exposez les domaines où vous excellez réellement.",
    },
    {
        icon: ShieldCheck,
        title: 'Rassurez les futurs vendeurs',
        desc: "Un profil vérifié, une affiliation confirmée et des avis sourcés inspirent immédiatement confiance.",
    },
    {
        icon: MapPin,
        title: 'Développez votre visibilité locale',
        desc: "Apparaissez auprès des vendeurs de votre secteur quand ils cherchent un professionnel sérieux.",
    },
    {
        icon: MessageSquare,
        title: 'Partagez une page professionnelle',
        desc: "Une URL claire à partager dans vos signatures, vos réseaux et vos prises de contact.",
    },
    {
        icon: TrendingUp,
        title: "Suivez l'évolution de votre réputation",
        desc: "Vues de profil, taux de réponse, retours vendeurs : vos statistiques personnelles, claires et utiles.",
    },
    {
        icon: Sparkles,
        title: 'Recevez des demandes qualifiées',
        desc: "Les vendeurs qui vous identifient via Homly arrivent déjà renseignés sur votre travail.",
    },
];

const BASE_FEATURES = [
    "Identité professionnelle vérifiée",
    "Fonction et spécialités essentielles",
    "Présence sur la page de votre agence",
    "Évaluations vendeurs liées à vos accompagnements",
    "Accès aux outils de suivi vendeur",
    "Historique de vos accompagnements dans Homly",
];

const PREMIUM_FEATURES = [
    "Page publique professionnelle dédiée",
    "URL personnelle partageable",
    "Présence dans le moteur de recherche public",
    "Statistiques personnelles détaillées",
    "Portfolio professionnel",
    "Outils de partage social",
    "Demandes vendeurs directement attribuées",
    "Continuité de l'historique professionnel",
];

export default function PourLesAgentsPage() {
    const [demoOpen, setDemoOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);

    const scrollToProfil = (e) => {
        e.preventDefault();
        const el = document.getElementById('profil');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="min-h-screen bg-[#050308] text-white antialiased" data-testid="pour-agents-page">
            <ConvNav onDemoClick={() => setDemoOpen(true)} />

            <main className="pt-28 md:pt-36 pb-24">
                {/* ════════ HERO ════════ */}
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[700px] h-[600px] rounded-full bg-[#A855F7]/22 blur-[160px]" />
                        <div className="absolute right-1/4 top-40 w-[420px] h-[420px] rounded-full bg-[#FF914D]/15 blur-[140px]" />
                    </div>

                    <div className="relative max-w-5xl mx-auto px-6 md:px-12">
                        <div className="mb-10">
                            <Link to="/"
                                  className="inline-flex items-center gap-2 text-sm text-[#A19DAB] hover:text-white transition-colors"
                                  data-testid="agents-back-link">
                                <ArrowLeft className="w-4 h-4" />
                                Retour à l'accueil
                            </Link>
                        </div>

                        <motion.span
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#A855F7]/30 bg-[#A855F7]/10 text-[10px] font-medium tracking-widest text-[#C7A1F0] uppercase"
                            style={MONO}
                        >
                            <User className="w-3 h-3" />
                            Pour les professionnels
                        </motion.span>

                        <motion.h1
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]"
                            style={DISPLAY}
                            data-testid="agents-hero-title"
                        >
                            Construisez une réputation professionnelle{' '}
                            <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                fondée sur votre travail réel.
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.12 }}
                            className="mt-6 text-base md:text-lg text-[#A19DAB] leading-relaxed max-w-3xl"
                        >
                            Homly transforme la qualité de vos accompagnements en preuves
                            visibles : avis vendeurs vérifiés, spécialités, expérience et
                            profil professionnel. Votre travail au quotidien construit
                            une réputation que vendeurs et agences peuvent reconnaître.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.18 }}
                            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                        >
                            <a
                                href="#profil"
                                onClick={scrollToProfil}
                                data-testid="agents-discover-profile"
                                className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#681793] to-[#A855F7] text-white font-semibold px-7 py-3.5 rounded-full shadow-xl shadow-[#A855F7]/30 hover:opacity-95 hover:scale-[1.02] transition-all"
                            >
                                Voir comment ça marche
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                            </a>
                            <button
                                type="button"
                                onClick={() => setInviteOpen(true)}
                                data-testid="agents-talk-agency-btn"
                                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium px-6 py-3.5 rounded-full border border-white/15 hover:border-white/30 transition-all"
                            >
                                <MessageSquare className="w-4 h-4 text-[#FF914D]" />
                                Inviter mon agence à Homly
                            </button>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="mt-6 text-xs text-[#6E6A7A] flex items-center gap-2"
                        >
                            <BadgeCheck className="w-3.5 h-3.5 text-[#C7A1F0]" />
                            Profils vérifiés · Affiliation confirmée · Avis issus de dossiers Homly
                        </motion.p>
                    </div>
                </section>

                {/* ════════ BÉNÉFICES ════════ */}
                <section id="profil" className="relative mt-24 md:mt-32 border-t border-white/5 pt-20">
                    <div className="max-w-6xl mx-auto px-6 md:px-12">
                        <div className="max-w-3xl">
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase" style={MONO}>
                                Votre identité professionnelle
                            </span>
                            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.1]" style={DISPLAY}>
                                Votre travail, enfin{' '}
                                <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                    visible et reconnu.
                                </span>
                            </h2>
                            <p className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed">
                                Huit bénéfices concrets pour les agents commerciaux, négociateurs et professionnels qui veulent valoriser leur expertise.
                            </p>
                        </div>

                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                            {BENEFITS.map(({ icon: Icon, title, desc }, i) => (
                                <motion.div
                                    key={title}
                                    initial={{ opacity: 0, y: 18 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ duration: 0.45, delay: i * 0.04 }}
                                    className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-all duration-500 hover:border-white/25 hover:bg-white/[0.04] hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(168,85,247,0.30)]"
                                    data-testid={`agents-benefit-${i}`}
                                >
                                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                         style={{ boxShadow: 'inset 0 1px 0 0 rgba(168,85,247,0.20)' }} />
                                    <div className="relative w-10 h-10 rounded-xl bg-[#A855F7]/12 flex items-center justify-center mb-4 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                                        <Icon className="w-5 h-5 text-[#C7A1F0]" />
                                    </div>
                                    <h3 className="relative font-semibold text-white mb-2 text-sm leading-snug" style={DISPLAY}>
                                        {title}
                                    </h3>
                                    <p className="relative text-sm text-[#A19DAB] leading-relaxed">{desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ════════ PORTABILITÉ — formulation équilibrée ════════ */}
                <section className="relative mt-24 md:mt-32 border-t border-white/5 pt-20">
                    <div className="max-w-5xl mx-auto px-6 md:px-12">
                        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#FF914D]/6 via-[#1A1224]/40 to-transparent p-8 md:p-12">
                            <div className="flex flex-col md:flex-row md:items-start gap-8">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 rounded-2xl bg-[#FF914D]/15 border border-[#FF914D]/25 flex items-center justify-center">
                                        <BadgeCheck className="w-7 h-7 text-[#FF914D]" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF914D]/25 bg-[#FF914D]/10 text-[10px] font-medium tracking-widest text-[#FF914D] uppercase" style={MONO}>
                                        Votre identité professionnelle
                                    </span>
                                    <h2 className="mt-4 text-2xl md:text-3xl font-bold tracking-tighter leading-[1.15]" style={DISPLAY}>
                                        Une réputation qui vous suit dans votre carrière.{' '}
                                        <span className="text-[#A19DAB]">Avec une frontière claire.</span>
                                    </h2>
                                    <p className="mt-4 text-sm md:text-base text-white/85 leading-relaxed">
                                        Votre identité professionnelle et les avis qui vous
                                        sont personnellement attribués peuvent accompagner
                                        votre évolution de carrière.
                                    </p>
                                    <p className="mt-3 text-sm md:text-base text-[#A19DAB] leading-relaxed">
                                        En revanche, <strong className="text-white">les données vendeurs,
                                        dossiers, documents et informations confidentielles
                                        restent la propriété et sous le contrôle de l'agence
                                        concernée.</strong> Les avis attribués à l'agence restent
                                        également rattachés à l'agence. Cette frontière nette
                                        protège tout le monde — vous, vos employeurs et les
                                        vendeurs qui vous font confiance.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ════════ DISTINCTION PROFIL BASE / ENRICHI ════════ */}
                <section className="relative mt-24 md:mt-32 border-t border-white/5 pt-20">
                    <div className="max-w-6xl mx-auto px-6 md:px-12">
                        <div className="max-w-3xl">
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase" style={MONO}>
                                Deux niveaux de profil
                            </span>
                            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.1]" style={DISPLAY}>
                                Travaillez avec votre agence.{' '}
                                <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                    Rendez-vous visible en option.
                                </span>
                            </h2>
                            <p className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed">
                                Avec l'abonnement de votre agence, vous accédez à tous les outils de suivi vendeur et votre identité pro apparaît sur la page agence. La <strong className="text-white">visibilité publique personnelle</strong> (page perso dédiée, URL, présence dans le moteur de recherche) est une option facultative — pour vous ou prise en charge par l'agence.
                            </p>
                        </div>

                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                            {/* Carte profil de base */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.55 }}
                                className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 md:p-8"
                                data-testid="agents-profile-base-card"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-white/80" />
                                    </div>
                                    <h3 className="text-lg md:text-xl font-bold text-white" style={DISPLAY}>
                                        Profil interne agence
                                    </h3>
                                </div>
                                <div className="mb-5">
                                    <span className="text-2xl md:text-3xl font-extrabold text-white tracking-tighter" style={DISPLAY}>
                                        Inclus
                                    </span>
                                    <span className="ml-2 text-sm text-[#A19DAB]">avec l'abonnement agence</span>
                                </div>
                                <p className="text-sm text-[#A19DAB] mb-5 leading-relaxed">
                                    Vous accédez à Homly et apparaissez sur la <strong className="text-white">page publique de votre agence</strong>. Pas de page personnelle dédiée à votre nom.
                                </p>
                                <ul className="space-y-2.5">
                                    {BASE_FEATURES.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-sm text-white/85">
                                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/60" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>

                            {/* Carte profil enrichi */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.55, delay: 0.08 }}
                                className="relative rounded-3xl border border-[#A855F7]/40 bg-gradient-to-b from-[#A855F7]/8 to-transparent p-7 md:p-8 shadow-2xl shadow-[#A855F7]/10"
                                data-testid="agents-profile-premium-card"
                            >
                                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#A855F7] text-[10px] font-bold tracking-widest text-white uppercase" style={MONO}>
                                    Visibilité publique
                                </span>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-[#A855F7]/15 flex items-center justify-center">
                                        <Sparkles className="w-5 h-5 text-[#C7A1F0]" />
                                    </div>
                                    <h3 className="text-lg md:text-xl font-bold text-white" style={DISPLAY}>
                                        Profil personnel public
                                    </h3>
                                </div>
                                <div className="mb-5">
                                    <span className="text-4xl md:text-5xl font-extrabold text-white tracking-tighter" style={DISPLAY}>
                                        19 €
                                    </span>
                                    <span className="ml-2 text-sm text-[#A19DAB]">HT / mois · sans engagement</span>
                                </div>
                                <p className="text-sm text-[#A19DAB] mb-5 leading-relaxed">
                                    Tout le profil de base, <strong className="text-white">plus une page publique à votre nom</strong>, votre propre URL et la présence dans le moteur de recherche Homly côté vendeurs.
                                </p>
                                <ul className="space-y-2.5">
                                    {PREMIUM_FEATURES.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-sm text-white/85">
                                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#C7A1F0]" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-6 text-xs text-[#A19DAB] leading-relaxed border-t border-white/[0.06] pt-4">
                                    <Briefcase className="w-3.5 h-3.5 inline-block mr-1 text-[#FF914D]" />
                                    Activation immédiate depuis votre espace Réseau, <strong className="text-white">directement
                                    par vous ou pris en charge par votre agence</strong> comme avantage
                                    professionnel. Résiliable à tout moment.
                                </p>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* ════════ CTA FINAL ════════ */}
                <section className="relative mt-24 md:mt-32 border-t border-white/5 pt-20">
                    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-[1.1]" style={DISPLAY}>
                            Faites de votre travail{' '}
                            <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                votre meilleure carte de visite.
                            </span>
                        </h2>
                        <p className="mt-4 text-base text-[#A19DAB] leading-relaxed">
                            Homly est conçu pour s'utiliser en équipe. Parlez-en à votre
                            agence — ou demandez-nous une démonstration ensemble.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                type="button"
                                onClick={() => setInviteOpen(true)}
                                data-testid="agents-final-cta"
                                className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#681793] to-[#A855F7] text-white font-semibold px-7 py-3.5 rounded-full shadow-xl shadow-[#A855F7]/30 hover:opacity-95 hover:scale-[1.02] transition-all"
                            >
                                Inviter mon agence à Homly
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <Link
                                to="/pour-les-agences"
                                data-testid="agents-cross-link"
                                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium px-6 py-3.5 rounded-full border border-white/15 hover:border-white/30 transition-all"
                            >
                                <Building2 className="w-4 h-4 text-[#FF914D]" />
                                Vous dirigez une agence ?
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <ConvFooter />
            <DemoRequestModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
            <InviteAgencyModal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />
        </div>
    );
}
